import React, { useRef, useState } from "react";
import { Button, Form, Input } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import "../auth.css";

/* ─── Icons ─────────────────────────────────────────────── */
const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const IconMail = () => (
  <svg className="auth-input-icon" viewBox="0 0 24 24">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const IconArrowLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const OTP_LENGTH = 6;

function VerifyOtp() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const [form]    = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [digits,  setDigits]  = useState(Array(OTP_LENGTH).fill(""));
  const refs = useRef([]);

 const handleDigit = (idx, val) => {
  // Allow letters and numbers
  const char = val.replace(/[^a-zA-Z0-9]/g, "").slice(-1);

  const next = [...digits];
  next[idx] = char;
  setDigits(next);

  if (char && idx < OTP_LENGTH - 1) {
    refs.current[idx + 1]?.focus();
  }
};

const handleKeyDown = (idx, e) => {
  if (e.key === "Backspace" && !digits[idx] && idx > 0) {
    refs.current[idx - 1]?.focus();
  }
};

const handlePaste = (e) => {
  // Allow alphanumeric paste
  const pasted = e.clipboardData
    .getData("text")
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, OTP_LENGTH);

  if (!pasted) return;

  e.preventDefault();

  const next = [...digits];

  pasted.split("").forEach((c, i) => {
    next[i] = c;
  });

  setDigits(next);

  refs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
};
  const onFinish = async (values) => {
    const otp = digits.join("");
    if (otp.length < OTP_LENGTH) { toast.error("Please enter the full 6-digit code"); return; }
    setLoading(true);
    try {
      dispatch(showLoading());
      const response = await axios.post("/api/user/verify-otp", { ...values, otp });
      dispatch(hideLoading());
      if (response.data.success) {
        toast.success(response.data.message);
        toast("Verification successful. Redirecting…");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        toast.error(response.data.message);
      }
    } catch {
      dispatch(hideLoading());
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-cloud auth-cloud-1" />
      <div className="auth-cloud auth-cloud-2" />
      <div className="auth-cloud auth-cloud-3" />

      <div className="auth-card">
        <div className="auth-icon-wrap success">
          <IconShield />
        </div>

        <h1 className="auth-title">Verify your identity</h1>
        <p className="auth-subtitle">
          We sent a 6-digit code to your email and WhatsApp.<br />Enter it below to continue.
        </p>

        <Form form={form} layout="vertical" onFinish={onFinish} className="auth-form">
          {/* Identifier field */}
          <Form.Item name="emailOrPhone"
            rules={[{ required: true, message: "Email or phone is required" }]}>
            <div className="auth-input-wrap">
              <IconMail />
              <Input placeholder="Email or Phone Number" size="large" />
            </div>
          </Form.Item>

          {/* OTP boxes */}
          <div>
            <div className="otp-row" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (refs.current[i] = el)}
                  className={`otp-box${d ? " filled" : ""}`}
                  value={d}
                  maxLength={1}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  onChange={(e) => handleDigit(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                />
              ))}
            </div>
            <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", marginTop: 10 }}>
              Didn't receive it?{" "}
              <span
                style={{ color: "#111827", fontWeight: 600, cursor: "pointer" }}
                onClick={() => toast("A new code has been sent")}
              >
                Resend code
              </span>
            </p>
          </div>

          <Form.Item style={{ marginTop: 4 }}>
            <Button htmlType="submit" loading={loading} className="auth-btn">
              {loading ? "Verifying…" : "Verify & Continue"}
            </Button>
          </Form.Item>
        </Form>

        <Link to="/login" className="auth-back">
          <IconArrowLeft /> Back to login
        </Link>
      </div>
    </div>
  );
}

export default VerifyOtp;